"use client";

import { COLOR, IllustrationCard } from "@/components/learn/chapter-1/_shared";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

const STRINGS: Record<
  Locale,
  {
    flowHeading: string;
    flowSub: string;
    companyProfit: string;
    whatNext: string;
    reinvest: string;
    reinvestItems: string[];
    reinvestOutcome: string;
    returnToShareholders: string;
    returnItems: string;
    returnOutcome: string;
    flowFooter: string;
    flowTitle: string;
    flowDescription: string;
    goodItems: { label: string; detail: string }[];
    badItems: { label: string; detail: string }[];
    compareHeading: string;
    compareSub: string;
    goodTitle: string;
    goodSubtitle: string;
    badTitle: string;
    badSubtitle: string;
    compareFooter: string;
    compareTitle: string;
    compareDescription: string;
    stages: { label: string; subLabel: string; note: string }[];
    stageHeading: string;
    stageSub: string;
    reinvestShort: string;
    dividendShort: string;
    dividendReturn: string;
    stageFooter: string;
    stageTitle: string;
    stageDescription: string;
  }
> = {
  ja: {
    flowHeading: "会社が稼いだ利益の使い道は一つではない",
    flowSub: "配当に回すか、事業に再投資するかは会社の判断",
    companyProfit: "会社の利益",
    whatNext: "ここから何に使うか",
    reinvest: "再投資に回す",
    reinvestItems: ["設備投資・研究開発・人材採用", "新規事業・M&A"],
    reinvestOutcome: "→ 将来の企業価値・株価の成長",
    returnToShareholders: "株主に還元する",
    returnItems: "配当金・自社株買い",
    returnOutcome: "→ 今すぐ現金で受け取れる",
    flowFooter: "どちらも株主リターンの形 — 配当がないこと自体は悪いことではない",
    flowTitle: "利益の使い道は「配当」だけではない",
    flowDescription:
      "会社が利益を配当に回さないのは、再投資で企業価値を増やす方が合理的だと判断しているからかもしれません。どちらも株主リターンの形です。",
    goodItems: [
      { label: "再投資先が明確", detail: "R&D・設備・新規事業" },
      { label: "売上・利益が伸びている", detail: "投資が結果につながっている" },
      { label: "資本効率が改善", detail: "ROE が上がっている" },
    ],
    badItems: [
      { label: "使い道が不明確", detail: "成長にも還元にも回らず" },
      { label: "業績が横ばいか低下", detail: "投資が結果に繋がっていない" },
      { label: "現金だけが積み上がる", detail: "有効活用されていない" },
    ],
    compareHeading: "同じ無配でも、中身はまったく違う",
    compareSub: "配当を出さない理由を確認すると、良い無配と注意したい無配が分かれる",
    goodTitle: "良い無配",
    goodSubtitle: "再投資が価値を生んでいる",
    badTitle: "注意したい無配",
    badSubtitle: "お金の行き先が見えにくい",
    compareFooter: "見るべきは「配当の有無」ではなく「利益の使い道に一貫性があるか」",
    compareTitle: "良い無配と注意したい無配は、ここが違う",
    compareDescription:
      "無配でも再投資で売上・利益・資本効率が伸びていれば健全です。使い道が不明確で成長にも還元にもつながっていない場合は注意が必要です。",
    stages: [
      { label: "成長初期", subLabel: "伸びしろが大きい", note: "ほぼ再投資が自然" },
      { label: "成長中期", subLabel: "拡大しつつ安定へ", note: "両方のバランス" },
      { label: "成熟期", subLabel: "安定・低成長", note: "還元が重視されやすい" },
    ],
    stageHeading: "成長段階によって、合理的なお金の使い方が変わる",
    stageSub: "伸びしろが大きい時期は再投資、成熟期は還元が自然になりやすい",
    reinvestShort: "再投資",
    dividendShort: "配当",
    dividendReturn: "配当・還元",
    stageFooter: "どの段階にいるかで、無配が合理的かどうかが変わる",
    stageTitle: "成長段階で「配当を出すべきか」は変わる",
    stageDescription:
      "成長初期は再投資が合理的、成熟期は還元が重視されやすくなります。無配が良いか悪いかは、その会社が今どの段階にいるかで判断が分かれます。",
  },
  en: {
    flowHeading: "There is more than one use for the profit a company earns",
    flowSub: "Whether it goes to dividends or back into the business is the company's call",
    companyProfit: "Company profit",
    whatNext: "What does it go to from here?",
    reinvest: "Reinvest it",
    reinvestItems: ["Capex, R&D, hiring", "New businesses, M&A"],
    reinvestOutcome: "→ Future company value and share-price growth",
    returnToShareholders: "Return it to shareholders",
    returnItems: "Dividends, buybacks",
    returnOutcome: "→ Cash you receive right now",
    flowFooter: "Both are forms of shareholder return — no dividend is not in itself bad",
    flowTitle: "Dividends are not the only use for profit",
    flowDescription:
      "A company not paying profit out as dividends may have judged that reinvesting to grow company value is more rational. Both are forms of shareholder return.",
    goodItems: [
      { label: "Clear reinvestment targets", detail: "R&D, capex, new businesses" },
      { label: "Revenue and profit growing", detail: "The investment is producing results" },
      { label: "Improving capital efficiency", detail: "ROE is rising" },
    ],
    badItems: [
      { label: "Unclear use of the money", detail: "Going to neither growth nor return" },
      { label: "Results flat or falling", detail: "The investment is not producing results" },
      { label: "Only cash piling up", detail: "Not being put to work" },
    ],
    compareHeading: "The same “no dividend” can mean very different things",
    compareSub: "Check why no dividend is paid and healthy cases separate from ones to watch",
    goodTitle: "Healthy no-dividend",
    goodSubtitle: "Reinvestment is creating value",
    badTitle: "No-dividend to watch",
    badSubtitle: "Where the money goes is hard to see",
    compareFooter: "What to look at is not “dividend or not” but “is the use of profit consistent?”",
    compareTitle: "Where healthy and worrying no-dividend cases differ",
    compareDescription:
      "Paying nothing is healthy if reinvestment is growing revenue, profit, and capital efficiency. Care is needed when the use is unclear and leads to neither growth nor return.",
    stages: [
      { label: "Early growth", subLabel: "A lot of room to grow", note: "Almost all reinvestment is natural" },
      { label: "Mid growth", subLabel: "Expanding towards stability", note: "A balance of both" },
      { label: "Mature", subLabel: "Stable, low growth", note: "Return tends to be weighted more" },
    ],
    stageHeading: "What counts as a rational use of money changes with the growth stage",
    stageSub: "Reinvestment is natural while there is room to grow; return becomes natural once mature",
    reinvestShort: "Reinvest",
    dividendShort: "Dividend",
    dividendReturn: "Dividends and return",
    stageFooter: "Whether no dividend is rational depends on which stage the company is in",
    stageTitle: "Whether a company “should pay a dividend” changes with its growth stage",
    stageDescription:
      "Reinvestment is rational in early growth; return tends to be weighted more once mature. Whether paying nothing is good or bad depends on which stage the company is in now.",
  },
};

// ── Colors ────────────────────────────────────────────────────────

const C = {
  reinvest: COLOR.sky,
  dividend: COLOR.emerald,
  warn: COLOR.amber,
  bad: COLOR.rose,
  accent: "#7c3aed",
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Diagram 1: Where does profit go? — Reinvest vs Distribute
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function ProfitFlowIllustration() {
  const t = STRINGS[useDocLocale()];
  const VW = 460;
  const VH = 220;
  const cx = VW / 2;

  // Source box
  const srcY = 44;
  const srcW = 130;
  const srcH = 38;

  // Two destination branches
  const branchY = 130;
  const boxW = 160;
  const boxH = 60;
  const leftCX = 120;
  const rightCX = 340;

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="mx-auto w-full max-w-xl">
      {/* Title */}
      <text x={cx} y={18} textAnchor="middle" fontSize="11" fill="currentColor" fontWeight="700">
        {t.flowHeading}
      </text>
      <text x={cx} y={31} textAnchor="middle" fontSize="8.5" fill="currentColor" fillOpacity={0.55}>
        {t.flowSub}
      </text>

      {/* Arrow marker */}
      <defs>
        <marker id="ndArrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M 0 0 L 6 3 L 0 6" fill="none" stroke="currentColor" strokeWidth="1" strokeOpacity={0.3} />
        </marker>
      </defs>

      {/* Source: Profit */}
      <rect
        x={cx - srcW / 2}
        y={srcY}
        width={srcW}
        height={srcH}
        rx={8}
        fill={C.accent}
        fillOpacity={0.08}
        stroke={C.accent}
        strokeWidth="1"
        strokeOpacity={0.35}
      />
      <text x={cx} y={srcY + 16} textAnchor="middle" fontSize="9" fill={C.accent} fontWeight="700">
        {t.companyProfit}
      </text>
      <text x={cx} y={srcY + 29} textAnchor="middle" fontSize="7.5" fill={C.accent} fillOpacity={0.6}>
        {t.whatNext}
      </text>

      {/* Branch arrows */}
      <path
        d={`M ${cx - 20} ${srcY + srcH + 2} Q ${cx - 20} ${srcY + srcH + 24} ${leftCX + boxW / 2} ${branchY - 2}`}
        fill="none"
        stroke={C.reinvest}
        strokeWidth="1.2"
        strokeOpacity={0.3}
        markerEnd="url(#ndArrow)"
      />
      <path
        d={`M ${cx + 20} ${srcY + srcH + 2} Q ${cx + 20} ${srcY + srcH + 24} ${rightCX - boxW / 2} ${branchY - 2}`}
        fill="none"
        stroke={C.dividend}
        strokeWidth="1.2"
        strokeOpacity={0.3}
        markerEnd="url(#ndArrow)"
      />

      {/* Left: Reinvest */}
      <rect
        x={leftCX - boxW / 2}
        y={branchY}
        width={boxW}
        height={boxH}
        rx={8}
        fill={C.reinvest}
        fillOpacity={0.06}
        stroke={C.reinvest}
        strokeWidth="1"
        strokeOpacity={0.3}
      />
      <text x={leftCX} y={branchY + 16} textAnchor="middle" fontSize="9.5" fill={C.reinvest} fontWeight="700">
        {t.reinvest}
      </text>
      <text x={leftCX} y={branchY + 30} textAnchor="middle" fontSize="7.5" fill={C.reinvest} fillOpacity={0.65}>
        {t.reinvestItems[0]}
      </text>
      <text x={leftCX} y={branchY + 42} textAnchor="middle" fontSize="7.5" fill={C.reinvest} fillOpacity={0.65}>
        {t.reinvestItems[1]}
      </text>
      <text x={leftCX} y={branchY + boxH + 14} textAnchor="middle" fontSize="8" fill={C.reinvest} fontWeight="600">
        {t.reinvestOutcome}
      </text>

      {/* Right: Dividend */}
      <rect
        x={rightCX - boxW / 2}
        y={branchY}
        width={boxW}
        height={boxH}
        rx={8}
        fill={C.dividend}
        fillOpacity={0.06}
        stroke={C.dividend}
        strokeWidth="1"
        strokeOpacity={0.3}
      />
      <text x={rightCX} y={branchY + 16} textAnchor="middle" fontSize="9.5" fill={C.dividend} fontWeight="700">
        {t.returnToShareholders}
      </text>
      <text x={rightCX} y={branchY + 30} textAnchor="middle" fontSize="7.5" fill={C.dividend} fillOpacity={0.65}>
        {t.returnItems}
      </text>
      <text x={rightCX} y={branchY + 42} textAnchor="middle" fontSize="7.5" fill={C.dividend} fillOpacity={0.65}></text>
      <text x={rightCX} y={branchY + boxH + 14} textAnchor="middle" fontSize="8" fill={C.dividend} fontWeight="600">
        {t.returnOutcome}
      </text>

      {/* Bottom note */}
      <text x={cx} y={VH - 4} textAnchor="middle" fontSize="8" fill="currentColor" fillOpacity={0.4}>
        {t.flowFooter}
      </text>
    </svg>
  );
}

export function DocProfitFlowDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard
      title={t.flowTitle}
      description={t.flowDescription}
    >
      <ProfitFlowIllustration />
    </IllustrationCard>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Diagram 2: Good no-dividend vs Concerning no-dividend
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

type CheckItem = { label: string; detail: string };

function NoDividendComparisonIllustration() {
  const t = STRINGS[useDocLocale()];
  const VW = 460;
  const VH = 210;
  const cx = VW / 2;
  const colW = 185;
  const leftCX = VW / 4 + 5;
  const rightCX = (VW * 3) / 4 - 5;
  const startY = 44;
  const rowH = 32;
  const rowGap = 6;

  function Column({
    items,
    color,
    title,
    subtitle,
    colCX,
  }: {
    items: CheckItem[];
    color: string;
    title: string;
    subtitle: string;
    colCX: number;
  }) {
    return (
      <g>
        <text x={colCX} y={startY} textAnchor="middle" fontSize="10" fill={color} fontWeight="700">
          {title}
        </text>
        <text x={colCX} y={startY + 13} textAnchor="middle" fontSize="7.5" fill={color} fillOpacity={0.6}>
          {subtitle}
        </text>
        {items.map((item, i) => {
          const ry = startY + 22 + i * (rowH + rowGap);
          return (
            <g key={item.label}>
              <rect
                x={colCX - colW / 2}
                y={ry}
                width={colW}
                height={rowH}
                rx={6}
                fill={color}
                fillOpacity={0.07}
                stroke={color}
                strokeWidth="0.8"
                strokeOpacity={0.3}
              />
              <text x={colCX - colW / 2 + 12} y={ry + 13} fontSize="8.5" fill={color} fontWeight="600">
                {item.label}
              </text>
              <text x={colCX - colW / 2 + 12} y={ry + 25} fontSize="7" fill={color} fillOpacity={0.55}>
                {item.detail}
              </text>
            </g>
          );
        })}
      </g>
    );
  }

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="mx-auto w-full max-w-xl">
      {/* Title */}
      <text x={cx} y={18} textAnchor="middle" fontSize="11" fill="currentColor" fontWeight="700">
        {t.compareHeading}
      </text>
      <text x={cx} y={31} textAnchor="middle" fontSize="8.5" fill="currentColor" fillOpacity={0.55}>
        {t.compareSub}
      </text>

      {/* Divider */}
      <line
        x1={cx}
        y1={38}
        x2={cx}
        y2={VH - 18}
        stroke="currentColor"
        strokeWidth="0.5"
        strokeOpacity={0.1}
        strokeDasharray="4 4"
      />

      <Column
        items={t.goodItems}
        color={C.reinvest}
        title={t.goodTitle}
        subtitle={t.goodSubtitle}
        colCX={leftCX}
      />
      <Column
        items={t.badItems}
        color={C.bad}
        title={t.badTitle}
        subtitle={t.badSubtitle}
        colCX={rightCX}
      />

      {/* Bottom note */}
      <text x={cx} y={VH - 4} textAnchor="middle" fontSize="8" fill="currentColor" fillOpacity={0.4}>
        {t.compareFooter}
      </text>
    </svg>
  );
}

export function DocNoDividendComparisonDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard
      title={t.compareTitle}
      description={t.compareDescription}
    >
      <NoDividendComparisonIllustration />
    </IllustrationCard>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Diagram 3: Growth stage → what's rational changes
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

type StageData = {
  stageIndex: number;
  reinvestPct: number;
  dividendPct: number;
};

const STAGES: StageData[] = [
  { stageIndex: 0, reinvestPct: 90, dividendPct: 10 },
  { stageIndex: 1, reinvestPct: 60, dividendPct: 40 },
  { stageIndex: 2, reinvestPct: 25, dividendPct: 75 },
];

function GrowthStageIllustration() {
  const t = STRINGS[useDocLocale()];
  const VW = 460;
  const VH = 230;
  const cx = VW / 2;

  const colCount = STAGES.length;
  const colW = 110;
  const gap = 24;
  const totalW = colCount * colW + (colCount - 1) * gap;
  const startX = (VW - totalW) / 2;

  const barMaxH = 110;
  const barW = 70;
  const barY = 70;

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="mx-auto w-full max-w-xl">
      {/* Title */}
      <text x={cx} y={18} textAnchor="middle" fontSize="11" fill="currentColor" fontWeight="700">
        {t.stageHeading}
      </text>
      <text x={cx} y={31} textAnchor="middle" fontSize="8.5" fill="currentColor" fillOpacity={0.55}>
        {t.stageSub}
      </text>

      {/* Arrow marker */}
      <defs>
        <marker id="ndStageArrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M 0 0 L 6 3 L 0 6" fill="none" stroke="currentColor" strokeWidth="1" strokeOpacity={0.25} />
        </marker>
      </defs>

      {STAGES.map((stage, i) => {
        const colStartX = startX + i * (colW + gap);
        const colCX = colStartX + colW / 2;
        const bx = colCX - barW / 2;

        const reinvestH = (stage.reinvestPct / 100) * barMaxH;
        const dividendH = barMaxH - reinvestH;

        // Dividend on top, reinvest on bottom
        const reinvestY = barY + dividendH;
        const dividendY = barY;

        return (
          <g key={stage.stageIndex}>
            {/* Column header */}
            <text x={colCX} y={barY - 16} textAnchor="middle" fontSize="8.5" fill="currentColor" fontWeight="600">
              {t.stages[stage.stageIndex]!.label}
            </text>
            <text x={colCX} y={barY - 5} textAnchor="middle" fontSize="7" fill="currentColor" fillOpacity={0.45}>
              {t.stages[stage.stageIndex]!.subLabel}
            </text>

            {/* Reinvest portion (bottom) */}
            <rect
              x={bx}
              y={reinvestY}
              width={barW}
              height={reinvestH}
              rx={4}
              fill={C.reinvest}
              fillOpacity={0.18}
              stroke={C.reinvest}
              strokeWidth="0.8"
              strokeOpacity={0.4}
            />
            {reinvestH > 20 ? (
              <g>
                <text
                  x={colCX}
                  y={reinvestY + reinvestH / 2 - 3}
                  textAnchor="middle"
                  fontSize="7.5"
                  fill={C.reinvest}
                  fontWeight="600"
                >
                  {t.reinvestShort}
                </text>
                <text
                  x={colCX}
                  y={reinvestY + reinvestH / 2 + 9}
                  textAnchor="middle"
                  fontSize="10"
                  fill={C.reinvest}
                  fontWeight="700"
                >
                  {stage.reinvestPct}%
                </text>
              </g>
            ) : (
              <text
                x={colCX}
                y={reinvestY + reinvestH / 2 + 3}
                textAnchor="middle"
                fontSize="7.5"
                fill={C.reinvest}
                fontWeight="600"
              >
                {stage.reinvestPct}%
              </text>
            )}

            {/* Dividend portion (top) */}
            {dividendH > 6 ? (
              <g>
                <rect
                  x={bx}
                  y={dividendY}
                  width={barW}
                  height={dividendH}
                  rx={4}
                  fill={C.dividend}
                  fillOpacity={0.15}
                  stroke={C.dividend}
                  strokeWidth="0.8"
                  strokeOpacity={0.4}
                />
                {dividendH > 20 ? (
                  <g>
                    <text
                      x={colCX}
                      y={dividendY + dividendH / 2 - 3}
                      textAnchor="middle"
                      fontSize="7.5"
                      fill={C.dividend}
                      fontWeight="600"
                    >
                      {t.dividendShort}
                    </text>
                    <text
                      x={colCX}
                      y={dividendY + dividendH / 2 + 9}
                      textAnchor="middle"
                      fontSize="10"
                      fill={C.dividend}
                      fontWeight="700"
                    >
                      {stage.dividendPct}%
                    </text>
                  </g>
                ) : (
                  <text
                    x={colCX}
                    y={dividendY + dividendH / 2 + 3}
                    textAnchor="middle"
                    fontSize="7"
                    fill={C.dividend}
                    fontWeight="600"
                  >
                    {stage.dividendPct}%
                  </text>
                )}
              </g>
            ) : null}

            {/* Note below bar */}
            <text
              x={colCX}
              y={barY + barMaxH + 14}
              textAnchor="middle"
              fontSize="7.5"
              fill="currentColor"
              fillOpacity={0.5}
            >
              {t.stages[stage.stageIndex]!.note}
            </text>

            {/* Arrow to next */}
            {i < colCount - 1 ? (
              <line
                x1={colStartX + colW + 4}
                y1={barY + barMaxH / 2}
                x2={colStartX + colW + gap - 4}
                y2={barY + barMaxH / 2}
                stroke="currentColor"
                strokeWidth="1"
                strokeOpacity={0.15}
                markerEnd="url(#ndStageArrow)"
              />
            ) : null}
          </g>
        );
      })}

      {/* Legend */}
      <rect
        x={startX}
        y={VH - 22}
        width={10}
        height={10}
        rx={2}
        fill={C.reinvest}
        fillOpacity={0.2}
        stroke={C.reinvest}
        strokeWidth="0.6"
      />
      <text x={startX + 14} y={VH - 13} fontSize="7.5" fill={C.reinvest} fontWeight="500">
        {t.reinvestShort}
      </text>
      <rect
        x={startX + 56}
        y={VH - 22}
        width={10}
        height={10}
        rx={2}
        fill={C.dividend}
        fillOpacity={0.2}
        stroke={C.dividend}
        strokeWidth="0.6"
      />
      <text x={startX + 70} y={VH - 13} fontSize="7.5" fill={C.dividend} fontWeight="500">
        {t.dividendReturn}
      </text>

      {/* Bottom note */}
      <text x={cx + 50} y={VH - 13} textAnchor="middle" fontSize="8" fill="currentColor" fillOpacity={0.4}>
        {t.stageFooter}
      </text>
    </svg>
  );
}

export function DocGrowthStageDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard
      title={t.stageTitle}
      description={t.stageDescription}
    >
      <GrowthStageIllustration />
    </IllustrationCard>
  );
}
